/* Établissement — D-31 (05/09) retired the six-pane page of D-19: opening an establishment
   now ENTERS ITS CONTEXT (lib/scope) and lands on Accueil; the panes became the real
   screens, filtered. `?vue=` deep links from before (liens, équipe, roues, supports,
   réglages) still land on the owning screen, in context. What survives here is the
   establishment's own page — its settings — shown by Établissements inside a context. */
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button, Card, Checkbox, Field, Hint, Input, KV, Note, PageHeader, Pill, Skeleton, Switch, useToast } from '@/ui'
import { useLocations, type Location } from '@/lib/api'
import { FENETRE_PRESETS, SECTEURS, getWindowDays, identitySentence, secteurLabel, sectorFloor, setIdentityFields, setScope, setSecteur, setWindowDays, useIdentityFields, useSecteur, useWindowDays, windowSentence } from '@/lib/scope'
import { REVIEW_URL_DIFFERS, REVIEW_URL_INVALID, REVIEW_URL_NOT_GOOGLE, bookingUrlCheck, getAdresse, getBookingUrl, getDestinations, ficheReviewUrl, getFiche, getFicheUrl, getReviewUrl, reviewUrlCheck, setBookingUrl, setReviewUrl } from '@/lib/campaign'
import { BOOKING_REVIEW, settled, toastWhenSaved, writeMark } from '@/lib/writes'
import { Wheel, getWheelDesign } from '@/lib/wheel'
import { identityKey } from '@/routes/Accueil'
import { Reglement } from '@/routes/cadeaux/Ledger'
import { EffetTable, ParamTabs, SETTINGS_READ_ONLY_LINE, canEditSettings } from '@/routes/parametres/ParamShell'

const VUE_TO_ROUTE: Record<string, string> = { liens: '/destinations', equipe: '/equipe', roues: '/cadeaux?onglet=roues', supports: '/supports', reglages: '/etablissements', apercu: '/' }

export default function EnterEtablissement() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const nav = useNavigate()
  useEffect(() => {
    if (id) setScope(id)
    const vue = params.get('vue') ?? 'apercu'
    nav(VUE_TO_ROUTE[vue] ?? '/', { replace: true })
  }, [id, params, nav])
  return null
}

/** « Fenêtre de participation » — THE place to set it (batch 13, Builder A's note ①): one value per
 *  establishment in lib/scope, read by the Parcours' rhythm screen (RJ-07) and printed by the rules.
 *  The presets are RJ-07's business shapes; anything under the sector's rhythm is shown and refused.
 *  A sector changed later never re-clamps a stored window (no silent change; the API refuses only a new
 *  window under the floor): the row says the window is under the rhythm, and what the next change must be. */
const underRhythm = (floor: number) => `sous le rythme du secteur (${floor} jours)\u00A0: votre prochaine modification sera d’au moins ${floor} jours.`
function FenetreRow({ locationId, tail, readOnly = false }: { locationId: string; tail: string; readOnly?: boolean }) {
  const days = useWindowDays(locationId)
  useSecteur() /* the floor is the sector's: read again when it changes */
  const toast = useToast()
  const [edit, setEdit] = useState(false)
  const [custom, setCustom] = useState('')
  const apply = (d: number) => {
    const mark = writeMark()
    const set = setWindowDays(locationId, d)
    setEdit(false); setCustom('')
    toastWhenSaved(mark, toast, `✓ Fenêtre de participation : ${windowSentence(set)} — le règlement le publie.`)
  }
  const floor = sectorFloor()
  if (!edit || readOnly) return <span><b>{windowSentence(days)}</b> {days < floor ? <span data-testid="sous-rythme">— {underRhythm(floor)}</span> : tail}{!readOnly && <> · <a href="#" onClick={e => { e.preventDefault(); setEdit(true) }}>modifier</a></>}</span>
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }} data-testid="fenetre-editeur">
      {FENETRE_PRESETS.map(p => {
        const under = p.days < floor
        return <Button key={p.id} size="sm" variant={p.days === days ? 'primary' : 'quiet'} disabled={under}
          title={under ? `Sous le rythme du secteur (${floor} jour${floor > 1 ? 's' : ''}) — pas proposé` : `${p.sectors} · ${p.plain}`}
          onClick={() => apply(p.days)}>{p.label} · {p.days} j</Button>
      })}
      <Input value={custom} onChange={e => setCustom(e.target.value.replace(/\D/g, ''))} placeholder="autre…" inputMode="numeric" aria-label="Fenêtre personnalisée, en jours" style={{ width: 88 }} />
      <Button size="sm" disabled={!custom} onClick={() => { const v = Number(custom); if (!v || v < floor) { toast({ message: `Pas moins de ${floor} jour${floor > 1 ? 's' : ''} — c’est le rythme du secteur.` }); return } apply(v) }}>OK</Button>
      <Button size="sm" variant="quiet" onClick={() => { setEdit(false); setCustom('') }}>Annuler</Button>
      <span className="f-help" style={{ flexBasis: '100%' }}>Jamais moins que le rythme du secteur ({floor} jour{floor > 1 ? 's' : ''}) — plus long, toujours possible. Le règlement public l’indique.</span>
    </span>
  )
}


/** « Secteur » (D-66) — chosen on day one, the floor of the participation window; changeable here. A floor
 *  now above the establishment's window is said in the toast, as on the window's row. */
function SecteurRow({ locationId, readOnly = false }: { locationId: string; readOnly?: boolean }) {
  const secteur = useSecteur()
  const [edit, setEdit] = useState(false)
  const toast = useToast()
  const pick = (x: typeof SECTEURS[number]) => {
    const mark = writeMark()
    setSecteur(x.id); setEdit(false)
    const w = getWindowDays(locationId)
    toastWhenSaved(mark, toast, `✓ Secteur : ${x.label} — rythme ${x.floor} jour${x.floor > 1 ? 's' : ''}.${w < x.floor ? ` Votre fenêtre, ${windowSentence(w)}, est ${underRhythm(x.floor)}` : ''} Vos cadeaux et votre roue ne changent pas.`)
  }
  if (!edit || readOnly) return <span><b>{secteurLabel(secteur)}</b>{secteur ? '' : ' (par défaut)'} — rythme du secteur {sectorFloor()} jour{sectorFloor() > 1 ? 's' : ''}{!readOnly && <> · <a href="#" onClick={e => { e.preventDefault(); setEdit(true) }}>changer</a></>}</span>
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }} data-testid="secteur-editeur">
      {SECTEURS.map(x => <Button key={x.id} size="sm" variant={x.id === secteur ? 'primary' : 'quiet'} onClick={() => pick(x)}>{x.label}</Button>)}
      <Button size="sm" variant="quiet" onClick={() => setEdit(false)}>Annuler</Button>
    </span>
  )
}


/** « Ce que le client renseigne » (D-69 ①) — e-mail always, prénom on by default. MA-16: the téléphone is out of V1 (no
 *  uniqueness, no till use, no mail): no toggle; lib keeps the field, off. The consent sentence sits with the Règlement (MA-18). */
function ChampsRow({ readOnly = false }: { readOnly?: boolean }) {
  const f = useIdentityFields()
  const toast = useToast()
  const flip = (k: 'prenom', v: boolean) => { const mark = writeMark(); const n = setIdentityFields({ [k]: v }); toastWhenSaved(mark, toast, `✓ Le client renseigne : ${identitySentence(n)}.`) }
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }} data-testid="champs-client">
      <span><b>E-mail</b> — toujours, pour que le cadeau le suive</span>
      <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><Switch checked={f.prenom} disabled={readOnly} onChange={e => flip('prenom', e.target.checked)} aria-label="Prénom" /> Prénom</label>
    </span>
  )
}

/** The establishment's own page — its settings (the former Paramètres pane), inside its context. */
export function EtablissementPage({ loc }: { loc: Location }) {
  const nav = useNavigate()
  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 className="t-page" style={{ margin: 0 }}>{loc.name}</h2>
        {loc.status === 'active' ? <Pill tone="ok">Prêt</Pill> : loc.status === 'pending' ? <Pill tone="warn">Fiche à lier</Pill> : <Pill tone="done">Archivé</Pill>}
        <Hint label="Ce qui se règle ici">Ses réglages seuls&nbsp;: fuseau, secteur, fenêtre de participation, archivage. Tout le reste — campagnes, cadeaux, supports, équipe, résultats — est dans les pages du même nom, filtrées sur cet établissement.</Hint>
      </div>
      <Card>
        <KV rows={[
          ['Fiche Google', <span key="1">{loc.status === 'pending' ? <><Pill tone="warn">À lier</Pill> <a href="#" onClick={e => { e.preventDefault(); nav('/destinations/ajouter') }}>lier la fiche</a></> : <><Pill tone="ok">Liée</Pill> {loc.rating ? `${loc.rating} ★` : ''} · rapport quotidien 06:00</>}</span>],
          ['Fuseau & horaires', <span key="h">Europe/Paris · lun–sam 9h–19h · <a href="#" onClick={e => e.preventDefault()}>modifier</a></span>],
          ['Secteur', <SecteurRow key="s" locationId={loc.id} readOnly={!canEditSettings()} />],
          ['Fenêtre de participation', <FenetreRow key="p" locationId={loc.id} tail="par roue de cet établissement" readOnly={!canEditSettings()} />],
          ['Ce que le client renseigne', <ChampsRow key="c" readOnly={!canEditSettings()} />],
          ['Archiver cet établissement', <span key="a"><a href="#" onClick={e => e.preventDefault()}>archiver</a> — l’attribution, le portefeuille et les conditions des cadeaux émis sont conservés</span>],
        ]} />
      </Card>
      <Note tone="candour">Ces réglages valent pour cet établissement seul. La marque, la facturation et les rôles se règlent pour tout le réseau — <a href="#" onClick={e => { e.preventDefault(); nav('/parametres/compte') }}>Paramètres</a>.</Note>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Button variant="quiet" onClick={() => nav('/supports')}>Ses supports</Button>
        <Button variant="quiet" onClick={() => nav('/cadeaux?onglet=roues')}>Ses roues</Button>
        <Button variant="quiet" onClick={() => nav('/campagnes')}>Ses campagnes</Button>
        <Button variant="quiet" onClick={() => nav('/equipe')}>Son équipe</Button>
      </div>
    </div>
  )
}

/* ---------- D-49 (05/09 night) · the one-location page — informations + marque + réglages ----------
   R5 v2 (vet 09/10, row A): « Informations » follows step 1 and the set of record (parcours-marchand-v2,
   Mon établissement). « Liée » is step 1's own rule — a fiche read, or a validated Google link of this
   establishment (GuideEtablissement), never loc.status — and what it shows is the fiche's: name, address,
   hours, rating. Not linked: « Pas encore liée. La lier » (step 1), with the name and address typed there
   if step 1 kept one. No value is written here: pasting, « Vérifier », « Plus tard » and the typed address
   live on step 1, and « Nom » is not a field (the fiche's, or step 1's; PATCH name comes with the wiring).
   The brand is Paramètres › Marque (D-70); this page shows it and links there.
   Point 9 (wireframes ruling 20/10 § 9, lib-pack-22): once a fiche is read, two Google fields — « Fiche Google » (the listing as linked,
   read here, changed on step 1) and « Lien d'avis Google » (where the customer writes: prefilled from the fiche, editable,
   emptied = back to the fiche's). Both are Google addresses; two that name different places are kept with one line. Google
   linked as a platform alone (no fiche) shows neither: its link is the platform's, edited in Paramètres › Plateformes.
   The fiche's own review link, the reference REVIEW_URL_DIFFERS compares with, is the lib's ficheReviewUrl() (lib-pack-23). */
const BOOKING_BAD = 'Un lien qui commence par https://, ou rien.'
export function MonEtablissement() {
  const { data: locations, isPending } = useLocations()
  if (isPending) return <div style={{ display: 'grid', gap: 10, maxWidth: 720 }}><Skeleton width="42%" /><Skeleton /><Skeleton width="65%" /></div>
  const loc = (locations ?? []).find(l => l.status !== 'inactive') ?? (locations ?? [])[0]
  if (!loc) return null
  return <MonEtablissementPage loc={loc} />
}
export function MonEtablissementPage({ loc }: { loc: Location }) {
  const nav = useNavigate()
  const toast = useToast()
  /* WF-126 (default, HANDOFF § 3): a Manager reads this page; the owner and an administrator write it */
  const readOnly = !canEditSettings()
  const design = getWheelDesign()
  const fiche = getFiche()
  const linked = !!fiche || getDestinations().some(d => d.locationId === loc.id && d.platform === 'google' && d.state === 'validee')
  const adresse = getAdresse()
  const name = fiche?.name ?? adresse?.name ?? loc.name
  const rating = fiche?.rating ?? loc.rating
  const [booking, setBooking] = useState(getBookingUrl() ?? '')
  const ficheUrl = fiche ? getFicheUrl() : null
  const [review, setReview] = useState(getReviewUrl() ?? '')
  const reviewIssue = fiche ? reviewUrlCheck(review) : null
  const reviewError = reviewIssue ? (reviewIssue.code === 'not_google' ? REVIEW_URL_NOT_GOOGLE : REVIEW_URL_INVALID) : undefined
  /* the line, never a refusal: the review link is not the one the fiche gave */
  const differs = !!fiche && !!review.trim() && !reviewIssue && review.trim() !== ficheReviewUrl()
  /* lib-pack-9's one rule: a page for writing a review is refused with its own reason (D-84; the API's links_tie_review) */
  const check = bookingUrlCheck(booking)
  const bookingError = 'refused' in check ? (check.reviewPage ? BOOKING_REVIEW : BOOKING_BAD) : undefined
  const save = () => {
    if (bookingError || reviewError) { toast({ message: (bookingError ?? reviewError)! }); return }
    const mark = writeMark()
    setBookingUrl(booking.trim() || null)
    if (fiche && review.trim() !== (getReviewUrl() ?? '')) setReviewUrl(review.trim() || null)
    try { localStorage.setItem(identityKey(), '1') } catch { /* fine */ }
    toastWhenSaved(mark, toast, '✓ Établissement enregistré — votre marque habille chaque écran client.', () => { setBooking(getBookingUrl() ?? ''); setReview(getReviewUrl() ?? '') })
    /* emptied: the field shows the fiche's link once the server has put it back (at once in the demo) */
    if (!review.trim()) void settled(mark).then(() => setReview(getReviewUrl() ?? ''))
  }
  /* a row the fiche did not give is left out (with an API, a fiche read with no Places key gives the link alone) */
  const info = ((linked
    ? [['Nom', name], ...(fiche ? [['Adresse', fiche.address], ['Horaires', fiche.hours]] : []), ...(rating ? [['Note', `${rating} ★`]] : [])]
    : adresse ? [['Nom', adresse.name], ['Adresse', adresse.address]] : []) as Array<[string, string]>).filter(([, v]) => !!v?.trim())
  return (
    <div className="page">
      {/* v3.3 · WF-037: the Paramètres strip — this page is its first tab */}
      <ParamTabs />
      <PageHeader title="Mon établissement"
        description={<>Votre lieu&nbsp;: ses informations, sa fiche Google, sa marque. <Hint label="Établissement">La fiche Google est le lien de vos avis et la source de vos informations (nom, adresse, note, horaires). La marque — votre logo, votre couleur — habille chaque écran que voit la cliente.</Hint></>}
        next={<>vos plateformes — <a href="#" onClick={e => { e.preventDefault(); nav('/parametres/plateformes') }}>Paramètres › Plateformes</a>.</>} />
      <div className="cols-2-1">
        <div style={{ display: 'grid', gap: 16 }}>
          <Card>
            <section className="esect" data-testid="informations">
              <p className="t-eyebrow"><span className="esect-n">A</span>Informations</p>
              <div style={{ display: 'grid', gap: 12 }}>
                {!linked && <p className="t-body" style={{ margin: 0 }} data-testid="fiche-a-lier">Pas encore liée. <a href="#" onClick={e => { e.preventDefault(); nav('/guide?etape=1') }}>La lier</a></p>}
                {info.length > 0 && <KV rows={info} />}
                {linked && <p className="f-help" style={{ margin: 0 }} data-testid="fiche-liee">Lus depuis la fiche chaque jour&nbsp;: rien à retaper.</p>}
                {fiche && <div style={{ display: 'grid', gap: 12 }} data-testid="champs-google">
                  {ficheUrl && <KV rows={[['Fiche Google', <span key="f" data-testid="fiche-google"><a href={ficheUrl} target="_blank" rel="noreferrer">{ficheUrl}</a>{!readOnly && <> · <a href="#" onClick={e => { e.preventDefault(); nav('/guide?etape=1') }}>changer</a></>}</span>]]} />}
                  <Field label="Lien d’avis Google" error={reviewError}
                    help={'Là où le client écrit son avis. Prérempli depuis la fiche\u00a0; videz-le pour revenir au lien de la fiche.'}>
                    {id => <Input id={id} value={review} disabled={readOnly} onChange={e => setReview(e.target.value)} placeholder="https://" inputMode="url" data-testid="lien-avis" />}</Field>
                  {differs && <Note tone="candour"><span data-testid="lien-avis-different">{REVIEW_URL_DIFFERS}</span></Note>}
                </div>}
                {readOnly && <p className="f-help" style={{ margin: 0 }} data-testid="lecture-seule">{SETTINGS_READ_ONLY_LINE}</p>}
                <Field label="Le lien de votre page de rendez-vous (facultatif)" error={bookingError}
                  help="S’il est rempli, la page du cadeau propose «&nbsp;Prendre rendez-vous&nbsp;» au client — jamais dans une demande d’avis.">
                  {id => <Input id={id} value={booking} disabled={readOnly} onChange={e => setBooking(e.target.value)} placeholder="https://" inputMode="url" data-testid="rendez-vous" />}</Field>
              </div>
            </section>
            <section className="esect">
              <p className="t-eyebrow"><span className="esect-n">B</span>Marque</p>
              {/* D-70 · the brand is its own page under Paramètres — one place, never two copies */}
              <p className="f-help" style={{ margin: 0 }}>Logo et couleur — réglés une fois pour tous vos écrans, dans <a href="#" onClick={e => { e.preventDefault(); nav('/identite') }}>Paramètres › Marque</a>.</p>
            </section>
            <section className="esect">
              <p className="t-eyebrow"><span className="esect-n">C</span>Réglages</p>
              <KV rows={[
                ['Fuseau', 'Europe/Paris'],
                ['Secteur', <SecteurRow key="s" locationId={loc.id} readOnly={readOnly} />],
                /* WF-037 · « Un client peut rejouer après — jours » is the app's Fenêtre de participation (presets, sector floor) */
                ['Fenêtre de participation', <FenetreRow key="p" locationId={loc.id} tail="— le règlement le publie" readOnly={readOnly} />],
                ['Ce que le client renseigne', <ChampsRow key="c" readOnly={readOnly} />],
              ]} />
              {/* WF-037 · « Un seul cadeau disponible à la fois par client »: the app's one rule (the builder says it at the gift step),
                  drawn here as the rule it is — checked, not a setting (⟦confirmable⟧ as a setting, HANDOFF § 3) */}
              <div data-testid="un-seul-cadeau" style={{ display: 'grid', gap: 6 }}>
                <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 13 }}><Checkbox checked readOnly disabled aria-label="Un seul cadeau disponible à la fois par client" /> Un seul cadeau disponible à la fois par client</label>
                <p className="f-help" style={{ margin: 0 }}>Avec cette règle, un cadeau encore disponible est présenté à la place d’un nouveau tirage. Les cadeaux déjà gagnés gardent leurs conditions.</p>
              </div>
            </section>
            <section className="esect" id="reglement">
              <p className="t-eyebrow"><span className="esect-n">D</span>Règlement du jeu</p>
              <p className="f-help" style={{ margin: '0 0 8px' }}>Le document de l’établissement — les chances de chaque roue en service, la validité, les plafonds, l’organisateur. Versionné&nbsp;: chaque cadeau gagné garde la sienne.</p>
              <p className="t-body" style={{ margin: '0 0 8px' }} data-testid="consentement">Ce que le client coche pour jouer&nbsp;: «&nbsp;J’accepte le règlement du jeu et la politique de confidentialité.&nbsp;» La même phrase pour toutes vos campagnes.</p>
              <Reglement />
            </section>
          </Card>
          <div className="actions">
            {!readOnly && <Button variant="primary" onClick={save}>Enregistrer</Button>}
            <Button variant="quiet" onClick={() => window.open('/d/demo?etape=spin', '_blank')}>Voir sur le parcours</Button>
            <span className="f-help"><b>Et ensuite&nbsp;:</b> vos liens d’avis, puis vos cadeaux.</span>
          </div>
          {/* WF-037 · rule 13: when a change takes effect — the same table on every Paramètres page */}
          <EffetTable />
        </div>
        <aside>
          <p className="t-eyebrow">Ce que voit la cliente</p>
          <Card>
            <p className="t-eyebrow" style={{ marginTop: 0 }}>Entrée</p>
            {design.logo && design.logoUrl && <img src={design.logoUrl} alt="Votre logo" data-testid="apercu-logo" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8, marginBottom: 6 }} />}
            <p className="t-body" style={{ fontFamily: 'Lora, Georgia, serif', fontSize: 17, margin: '0 0 4px' }}>Un cadeau vous attend chez {name}.</p>
            <p className="f-help" style={{ margin: 0 }}>Scannez, jouez — avec ou sans avis.</p>
          </Card>
          <Card>
            <p className="t-eyebrow" style={{ marginTop: 0 }}>Jeu</p>
            <div style={{ display: 'grid', placeItems: 'center' }}><Wheel design={design} size={150} /></div>
            <p className="f-help" style={{ textAlign: 'center', margin: '8px 0 0' }} data-testid="apercu-marque">Votre logo et votre couleur, réglés dans <a href="#" onClick={e => { e.preventDefault(); nav('/identite') }}>Paramètres › Marque</a>.</p>
          </Card>
        </aside>
      </div>
    </div>
  )
}
